<template>
  <WdsStack v-if="hire" direction-column class="tw-rounded">
    <WdsStack
      justify-space-between
      align-center
      class="tw-bg-true-gray-50 tw-border-b tw-border-gray-200 tw-px-4 tw-py-3"
    >
      <WdsStack gap-sm align-center>
        <WdsHeading type="title3" class="tw-pl-2">
          {{ $t('Internal Fields') }}
        </WdsHeading>
        <WdsLabel xs subtle>
          <WdsStack gap-xs align-center class="tw-items-center">
            <WdsIcon :size="16" name="locked" />
            <WdsText :text="$t('Not shown to the worker')" />
          </WdsStack>
        </WdsLabel>
      </WdsStack>
      <WdsButtonGhost>
        <a
          :href="
            laravelNamedRoute('yjob.edit', {
              yjob: hire.job?.id,
              step: 'custom-fields',
              // eslint-disable-next-line vue/camelcase -- Baseline eslint ignore please fix if working on this
              redirect_uri: laravelNamedRoute('hires.hire.view', {
                hire: hire.id,
                tab: 'overview',
              }),
            })
          "
        >
          <WdsStack align-center gap-sm>
            {{ $t('Manage') }}
            <WdsIcon :size="16" name="chevron-right" />
          </WdsStack>
        </a>
      </WdsButtonGhost>
    </WdsStack>
    <WdsGrid columns="2" row-gap-md class="tw-px-4 tw-pt-4 tw-pb-2">
      <WdsText :text="(owners?.length ?? 1) > 1 ? $t('Owners') : $t('Owner')" />
      <WdsStack v-if="owners" class="tw-flex tw-flex-wrap">
        <WdsText
          v-for="(owner, index) in owners"
          :key="owner"
          :text="`${owner}${index < owners.length - 1 ? ',' : ''}`"
          :class="index < owners.length - 1 ? 'tw-pr-1' : ''"
        />
      </WdsStack>

      <template v-if="isCompany">
        <WdsText text="Internal budget" />
        <WdsText
          :secondary="!internalBudget"
          :text="internalBudget ? internalBudget + ' ' + hire.job.currency : $t('Not specified')"
        />
      </template>

      <template v-if="projectTrackingEnabled">
        <WdsText text="Project" />
        <WdsText
          :secondary="!hire.job.project"
          :text="hire.job.project?.name ?? $t('Not specified')"
        />
      </template>

      <WdsText v-if="customFields && customFields?.length > 0" :text="$t('Custom fields')" />
    </WdsGrid>
    <div class="tw-px-4 tw-w-full">
      <CustomFieldValuesDisplay
        v-if="customFields && customFields?.length > 0"
        :custom-field-values="customFields"
        class="tw-w-full"
      />
    </div>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsText,
  WdsIcon,
  WdsStack,
  WdsButtonGhost,
  WdsGrid,
  WdsHeading,
  WdsLabel,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed } from 'vue'
import { graphql, useFragment } from '@/gql'
import { HireFragment, HireJobBudgetFragment } from '@/components/Hire/graphql/fragments'
import { $t } from '@/composables/translation'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import CustomFieldValuesDisplay from '@/components/Fieldable/CustomFieldValuesDisplay.vue'
import { useAccountSettings } from '@/composables/useAccountSettings'
import AccountType from '@/utils/AccountType'

const props = defineProps<{ hireId: string }>()

const { result } = useQuery(
  graphql(`
    query HireInternalFields($id: ID!) {
      hire(id: $id) {
        ...HireType
      }
    }
  `),
  {
    id: props.hireId,
  },
  {
    clientId: 'publicApi',
  },
)

const isCompany = computed(() => AccountType.isCompany(currentAccountType))

const { result: hireJobBudgetResult } = useQuery(
  graphql(`
    query HireJobBudget($id: ID!) {
      hire(id: $id) {
        ...HireJobBudget
      }
    }
  `),
  {
    id: props.hireId,
  },
  {
    clientId: 'publicApi',
    enabled: isCompany,
  },
)

const hire = computed(() => useFragment(HireFragment, result.value?.hire))
const projectTrackingEnabled = useAccountSettings('project_tracking')
const owners = computed(() => hire.value?.job.owners.map((owner) => owner.name))

const internalBudget = computed(
  () =>
    useFragment(HireJobBudgetFragment, hireJobBudgetResult.value?.hire)?.job?.internalBudget ?? '',
)
const customFields = computed(() => hire.value?.job.customFieldValues)
</script>

<style lang="scss" scoped>
.gradient-bar {
  background: linear-gradient(90deg, #fd8a33 0%, #fbc5c5 40%, #ffc89e 100%);
  border-radius: 3px 3px 0 0;
}

.section {
  border-radius: 0 0 3px 3px;
}
</style>
